import React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import FotoPacks from "../components/fotografia/FotoPacks";
import FotoFaq from "../components/fotografia/FotoFaq";
import FotoProceso from "../components/fotografia/FotoProceso";
import FotoGaleria from "../components/fotografia/FotoGaleria";
import FotoCtaFinal from "../components/fotografia/FotoCtaFinal";
import FotoStickyCta from "../components/fotografia/FotoStickyCta";
import FotoComparativa from "../components/fotografia/FotoComparativa";
import FondoEstudio from "../components/fotografia/FondoEstudio";
import WhatsAppCta from "../components/fotografia/WhatsAppCta";
import { fotoPrincipal } from "../data/portafolio";
import { clientesDe } from "../data/clientes";
import { etiquetaPrecio } from "../data/precios";
import {
  desdeIvaIncluido,
  fotosDelSegmento,
  precioDesde,
  segmentoPorId,
  segmentoPorPath,
  subSegmentosDe,
} from "../data/segmentos";
import { breadcrumbJsonLd, faqJsonLd, segmentoOffersJsonLd } from "../seo/config";
import PhotoImage from "../components/fotografia/PhotoImage";
import { SIZES } from "../utils/photos";

const SegmentoPage = () => {
  const { pathname } = useLocation();
  const segmento = segmentoPorPath(pathname);

  if (!segmento) return <Navigate to="/" replace />;

  const desde = precioDesde(segmento);
  const heroFoto = fotoPrincipal(segmento.id, segmento.portada);
  const clientes = clientesDe(segmento.id);
  const relacionados = segmento.subDe
    ? [segmentoPorId(segmento.subDe), ...subSegmentosDe(segmento.subDe).filter((s) => s.id !== segmento.id)]
    : subSegmentosDe(segmento.id);
  const crumbs = [
    { name: "Inicio", path: "/" },
    { name: segmento.nombre, path: segmento.path },
  ];

  return (
    <>
      <Seo
        title={segmento.seo.title}
        description={segmento.seo.description}
        keywords={segmento.seo.keywords}
        path={segmento.path}
        jsonLd={[
          breadcrumbJsonLd(crumbs),
          segmentoOffersJsonLd(segmento),
          faqJsonLd(segmento.faqs),
        ]}
      />

      {/*
        El hero solo se parte en dos columnas cuando hay una foto propia de
        este segmento. Sin ella, la segunda columna era un rectángulo gris —
        que en la web de un fotógrafo no se lee como "pendiente", se lee como
        una imagen rota. Mientras no llega, el hero ocupa el ancho completo
        sobre la textura de estudio.
      */}
      <section
        id="inicio"
        className="relative overflow-hidden bg-navy-900 pb-16 pt-28 text-white md:pb-24 md:pt-32"
      >
        {heroFoto ? null : <FondoEstudio priority />}
        <div
          className={`container-page relative z-10 grid items-center gap-12 ${
            heroFoto ? "lg:grid-cols-2" : ""
          }`}
        >
          <div className={heroFoto ? "" : "max-w-3xl"}>
            <p className="text-sand-300">{segmento.paraQuien}</p>
            <h1 className="mt-4 font-display text-display-md font-bold text-white">{segmento.h1}</h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-navy-100">{segmento.promesa}</p>
            <p className="mt-4 max-w-xl text-navy-200">{segmento.dolor}</p>
            <p className="mt-8 font-display text-4xl font-extrabold tabular-nums">
              {etiquetaPrecio(desde, { ivaIncluido: desdeIvaIncluido(segmento) })}
              <span className="ml-2 text-lg font-medium text-navy-200">
                {segmento.packs.length > 1 ? "desde" : `por ${segmento.unidad}`}
              </span>
            </p>
            <p className="mt-2 text-sm text-navy-300">
              {segmento.lugar}. Entrega en {segmento.entrega}. Factura.
            </p>
            {segmento.entregaNota ? (
              <p className="mt-1 text-sm text-sand-300">{segmento.entregaNota}</p>
            ) : null}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <WhatsAppCta message={segmento.whatsapp} location="segmento_hero" className="w-full sm:w-auto">
                Pedir fecha
              </WhatsAppCta>
              <Link to="/precios" className="btn-on-dark-outline w-full sm:w-auto">
                Ver todos los precios
              </Link>
            </div>
          </div>
          {heroFoto ? (
            <div className="overflow-hidden rounded-2xl">
              <PhotoImage
                foto={heroFoto}
                bn
                ratio={0.8}
                sizes={SIZES.half}
                priority
                className="h-full w-full object-cover"
                alt={heroFoto.alt || segmento.nombre}
              />
            </div>
          ) : null}
        </div>
      </section>

      <FotoPacks segmento={segmento} />
      <FotoComparativa segmento={segmento} />

      {segmento.entregables?.length ? (
        <section className="bg-white py-16">
          <div className="container-page">
            <h2 className="section-title">Qué te llevas.</h2>
            <ul className="mt-8 grid gap-4 md:grid-cols-2">
              {segmento.entregables.map((item) => (
                <li key={item} className="border-t border-navy-100 pt-4 text-navy-700">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <FotoGaleria
        segmentoId={segmento.id}
        nombre={segmento.nombre}
        whatsapp={segmento.whatsapp}
        fotos={fotosDelSegmento(segmento)}
      />

      {/* Enlace entre el servicio y sus páginas de búsqueda, en los dos
          sentidos: sin él, la página de revelación queda huérfana y Google
          no le pasa la autoridad de eventos. */}
      {relacionados.length ? (
        <section className="bg-white py-12">
          <div className="container-page">
            <p className="text-sm font-semibold text-navy-900">También fotografiamos</p>
            <ul className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
              {relacionados.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    className="text-navy-700 underline decoration-sand-400 decoration-2 underline-offset-4 hover:text-navy-900"
                  >
                    {item.h1}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}
      {clientes.length ? (
        <section className="bg-sand-100 py-16">
          <div className="container-page">
            <h2 className="section-title max-w-2xl">Para quién se hizo.</h2>
            <ul className="mt-8 space-y-6">
              {clientes.map((cliente) => (
                <li key={cliente.id} className="border-t border-navy-200 pt-6">
                  <p className="font-display text-2xl font-bold text-navy-900">{cliente.nombre}</p>
                  <p className="mt-1 text-sm text-navy-500">{cliente.rubro}</p>
                  <p className="mt-3 max-w-2xl text-navy-700">{cliente.trabajo}.</p>
                  {cliente.instagram ? (
                    <a
                      href={cliente.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-block text-sm font-semibold text-navy-900 underline decoration-sand-400 decoration-2 underline-offset-4 hover:decoration-navy-900"
                    >
                      Ver {cliente.handle}
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <FotoProceso />
      <FotoFaq faqs={segmento.faqs} whatsapp={segmento.whatsapp} />
      <FotoCtaFinal segmento={segmento} />
      <FotoStickyCta segmento={segmento} />
    </>
  );
};

export default SegmentoPage;
